import { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, CircularProgress,
  Alert, IconButton, Tooltip, Paper, Stack, ToggleButtonGroup, ToggleButton,
  Popover, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import { ChevronLeft, ChevronRight, PictureAsPdf } from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const TYPE_COLOR = {
  'Long Video':  { bg: '#1A237E', text: '#fff' },  // deep indigo
  'Short Video': { bg: '#BF360C', text: '#fff' },  // deep burnt orange
  'Ad':          { bg: '#1B5E20', text: '#fff' },  // deep forest green
};
const DEFAULT_COLOR  = { bg: '#4A148C', text: '#fff' }; // deep purple
const HIGHLIGHT_COLOR = { bg: '#E65100', text: '#fff' }; // deep amber — "you're in this shoot"

const DAYS   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function isMyCrew(ev, username) {
  if (!username) return false;
  if (ev.directorId === username) return true;
  if (ev.cinematographerId === username) return true;
  const ids = Array.isArray(ev.castingIds)
    ? ev.castingIds
    : (() => { try { return JSON.parse(ev.castingIds || '[]'); } catch (_) { return []; } })();
  return ids.includes(username);
}

function ShootCalendar() {
  const { user } = useAuth();
  const todayDate   = new Date();
  const tomorrowDate = new Date(todayDate); tomorrowDate.setDate(todayDate.getDate() + 1);
  const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const todayStr    = fmt(todayDate);
  const tomorrowStr = fmt(tomorrowDate);

  const [year, setYear]   = useState(todayDate.getFullYear());
  const [month, setMonth] = useState(todayDate.getMonth());
  const [monthEvents, setMonthEvents] = useState([]); // for calendar grid dots
  const [detailEvents, setDetailEvents] = useState({}); // { dateStr: [events] }
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null); // null = show today+tomorrow default

  // Export PDF state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportNotes, setExportNotes] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportRange, setExportRange] = useState('monthly'); // 'monthly' | 'weekly'
  const [scheduleFilter, setScheduleFilter] = useState('all');
  const [popover, setPopover] = useState({ anchor: null, event: null });

  // Load full month for grid markers
  useEffect(() => {
    fetchMonth();
  }, [year, month]); // eslint-disable-line

  // On mount load today + tomorrow for the default list
  useEffect(() => {
    fetchDetailDates([todayStr, tomorrowStr]);
  }, []); // eslint-disable-line

  const fetchMonth = async () => {
    setLoadingMonth(true);
    try {
      const firstDay = `${year}-${String(month+1).padStart(2,'0')}-01`;
      const lastDay  = fmt(new Date(year, month+1, 0));
      const res = await api.get(`/shoot-plans/calendar?startDate=${firstDay}&endDate=${lastDay}`);
      setMonthEvents(res.data.data.events || []);
    } catch (_) { setError('Failed to load calendar'); }
    finally { setLoadingMonth(false); }
  };

  const fetchDetailDates = async (dates) => {
    setLoadingDetail(true);
    try {
      const results = await Promise.all(dates.map(d =>
        api.get(`/shoot-plans/calendar?startDate=${d}&endDate=${d}`)
          .then(r => ({ date: d, events: r.data.data.events || [] }))
          .catch(() => ({ date: d, events: [] }))
      ));
      const map = {};
      results.forEach(r => { map[r.date] = r.events; });
      setDetailEvents(map);
    } finally { setLoadingDetail(false); }
  };

  const prevMonth = () => {
    setSelectedDate(null);
    if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    setSelectedDate(null);
    if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
  };

  const handleDayClick = (dateStr) => {
    if (selectedDate === dateStr) { setSelectedDate(null); return; }
    setSelectedDate(dateStr);
    if (!detailEvents[dateStr]) {
      fetchDetailDates([dateStr]);
    }
  };

  // Events for the grid (month-level, markers only)
  const monthEventMap = {};
  monthEvents.forEach(e => {
    if (!monthEventMap[e.date]) monthEventMap[e.date] = [];
    monthEventMap[e.date].push(e);
  });

  // Events for detail list
  const getDetailForDate = (dateStr) => {
    const evs = detailEvents[dateStr] || [];
    return scheduleFilter === 'mine' ? evs.filter(e => isMyCrew(e, user?.username)) : evs;
  };

  // Dates to show in the detail section
  const detailDates = selectedDate ? [selectedDate] : [todayStr, tomorrowStr];

  const myEvents = monthEvents.filter(e => isMyCrew(e, user?.username));

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth     = new Date(year, month+1, 0).getDate();

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      let startDay, endDay, rangeLabel;

      if (exportRange === 'weekly') {
        // Current calendar week: Monday to Sunday
        const now = new Date();
        const dow = now.getDay(); // 0=Sun
        const diffToMon = dow === 0 ? -6 : 1 - dow;
        const mon = new Date(now); mon.setDate(now.getDate() + diffToMon); mon.setHours(0,0,0,0);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        startDay = fmt(mon);
        endDay   = fmt(sun);
        rangeLabel = `Week of ${mon.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} – ${sun.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      } else {
        startDay   = `${year}-${String(month+1).padStart(2,'0')}-01`;
        endDay     = fmt(new Date(year, month+1, 0));
        rangeLabel = `${MONTHS[month]} ${year}`;
      }
      const res = await api.get(`/shoot-plans/calendar?startDate=${startDay}&endDate=${endDay}`);
      const allEvents = res.data.data.events || [];

      // Group by date, sort by date
      const byDate = {};
      allEvents.forEach(ev => {
        if (!byDate[ev.date]) byDate[ev.date] = [];
        byDate[ev.date].push(ev);
      });
      const sortedDates = Object.keys(byDate).sort();

      const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

      const rows = sortedDates.flatMap(dateStr => {
        const d = new Date(dateStr + 'T00:00:00');
        const dayName = DAY_NAMES[d.getDay()];
        const dateLabel = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
        return byDate[dateStr].map(ev => {
          const castNames = Array.isArray(ev.castingList)
            ? ev.castingList
            : (() => { try { return JSON.parse(ev.castingList || '[]'); } catch (_) { return []; } })();
          return { dateLabel, dayName, director: ev.director || '—', title: ev.contentTitle || '—', artists: castNames.join(', ') || '—' };
        });
      });

      const notesHtml = exportNotes.trim()
        ? `<div class="notes"><strong>Notes:</strong> ${exportNotes.trim().replace(/\n/g, '<br/>')}</div>`
        : '';

      const tableRows = rows.map(r => `
        <tr>
          <td>${r.dateLabel}</td>
          <td>${r.dayName}</td>
          <td>${r.director}</td>
          <td>${r.title}</td>
          <td>${r.artists}</td>
        </tr>`).join('');

      const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Shoot Plan – ${rangeLabel}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 32px; color: #111; }
  h1 { text-align: center; font-size: 22px; background: #1a237e; color: #fff; padding: 12px; border-radius: 4px; margin-bottom: 6px; }
  h2 { text-align: center; font-size: 14px; color: #555; margin: 0 0 16px; }
  .notes { background: #fff8e1; border-left: 4px solid #f9a825; padding: 8px 12px; margin-bottom: 16px; font-size: 13px; border-radius: 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead tr { background: #e8eaf6; }
  th { padding: 8px 10px; text-align: left; font-weight: 700; border-bottom: 2px solid #9fa8da; }
  td { padding: 7px 10px; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
  tr:nth-child(even) td { background: #f5f5f5; }
  @media print { body { margin: 16px; } }
</style>
</head>
<body>
  <h1>THIS ${exportRange === 'weekly' ? 'WEEK' : 'MONTH'} PLAN</h1>
  <h2>${rangeLabel}</h2>
  ${notesHtml}
  <table>
    <thead>
      <tr>
        <th>Date</th><th>Day</th><th>Assigned To</th><th>Project / Task</th><th>Artist</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`;

      const printWin = window.open('', '_blank');
      printWin.document.write(html);
      printWin.document.close();
      printWin.focus();
      setTimeout(() => { printWin.print(); }, 400);
      setExportDialogOpen(false);
      setExportNotes('');
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExportLoading(false);
    }
  };
  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Shoot Calendar</Typography>
          <Typography variant="body2" color="text.secondary">Confirmed shoot dates across all content</Typography>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          {/* Export PDF */}
          <Button
            variant="outlined" size="small" startIcon={<PictureAsPdf />}
            onClick={() => setExportDialogOpen(true)}
            sx={{ borderColor: '#c62828', color: '#c62828', '&:hover': { borderColor: '#b71c1c', bgcolor: '#ffebee' } }}
          >
            Export PDF
          </Button>
          <ToggleButtonGroup
            value={scheduleFilter}
            exclusive
            onChange={(_, v) => { if (v) setScheduleFilter(v); }}
            size="small"
          >
            <ToggleButton value="all" sx={{ fontSize: '0.75rem', px: 2 }}>All Schedules</ToggleButton>
            <ToggleButton value="mine" sx={{ fontSize: '0.75rem', px: 2, color: HIGHLIGHT_COLOR.bg,
              '&.Mui-selected': { bgcolor: HIGHLIGHT_COLOR.bg, color: '#fff', '&:hover': { bgcolor: '#E65100' } }
            }}>My Shoots</ToggleButton>
          </ToggleButtonGroup>
          {/* Legend */}
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {Object.entries(TYPE_COLOR).map(([type, col]) => (
              <Chip key={type} label={type} size="small" sx={{ bgcolor: col.bg, color: col.text, fontSize: '0.7rem' }} />
            ))}
            <Chip label="My Shoot" size="small" sx={{ bgcolor: HIGHLIGHT_COLOR.bg, color: HIGHLIGHT_COLOR.text, fontSize: '0.7rem', fontWeight: 700 }} />
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {myEvents.length > 0 && scheduleFilter === 'all' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have <strong>{myEvents.length}</strong> confirmed shoot{myEvents.length !== 1 ? 's' : ''} this month.
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Month navigation */}
          <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
            <IconButton onClick={prevMonth}><ChevronLeft /></IconButton>
            <Typography variant="h5" fontWeight={700} minWidth={200} textAlign="center">
              {MONTHS[month]} {year}
            </Typography>
            <IconButton onClick={nextMonth}><ChevronRight /></IconButton>
          </Box>

          {loadingMonth ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : (
            <>
              {/* Day headers */}
              <Grid container columns={7} sx={{ mb: 0.5 }}>
                {DAYS.map(d => (
                  <Grid item xs={1} key={d}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      textAlign="center" display="block"
                      sx={{ color: d === 'Sun' ? 'error.main' : d === 'Sat' ? 'warning.main' : 'inherit' }}>
                      {d}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar cells */}
              <Grid container columns={7}>
                {cells.map((day, idx) => {
                  if (!day) return <Grid item xs={1} key={`empty-${idx}`}><Box sx={{ minHeight: 80, p: 0.5 }} /></Grid>;

                  const dateStr    = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayEvents  = monthEventMap[dateStr] || [];
                  const isToday    = dateStr === todayStr;
                  const isSelected = dateStr === selectedDate;
                  const dayOfWeek  = new Date(year, month, day).getDay();
                  const isWeekend  = dayOfWeek === 0 || dayOfWeek === 6;
                  const hasMyShoot = dayEvents.some(e => isMyCrew(e, user?.username));

                  return (
                    <Grid item xs={1} key={dateStr}>
                      <Paper
                        variant="outlined"
                        onClick={() => handleDayClick(dateStr)}
                        sx={{
                          minHeight: 80, p: 0.5,
                          bgcolor: isSelected ? '#E8EAF6'
                            : isToday ? '#E3F2FD'
                            : isWeekend ? '#FFF8E1'
                            : 'background.paper',
                          border: isSelected ? '2px solid #3949AB'
                            : isToday ? '2px solid #1565C0'
                            : hasMyShoot ? '2px solid #FF6F00'
                            : undefined,
                          borderRadius: 1, m: 0.25,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: isSelected ? '#E8EAF6' : '#F3F4FF' }
                        }}
                      >
                        <Typography variant="caption" fontWeight={isToday ? 800 : 400}
                          color={isToday ? 'primary' : isWeekend ? 'warning.dark' : 'text.secondary'}
                          display="block" textAlign="right">
                          {day}
                        </Typography>
                        <Stack spacing={0.25}>
                          {dayEvents.map((ev, i) => {
                            const mine = isMyCrew(ev, user?.username);
                            const col  = mine ? HIGHLIGHT_COLOR : (TYPE_COLOR[ev.contentType] || DEFAULT_COLOR);
                            const castNames = Array.isArray(ev.castingList)
                              ? ev.castingList
                              : (() => { try { return JSON.parse(ev.castingList || '[]'); } catch (_) { return []; } })();
                            return (
                              <Tooltip key={i} arrow placement="top" disableTouchListener title={
                                <Box sx={{ maxWidth: 240 }}>
                                  <Typography variant="subtitle2" fontWeight={700}>{ev.contentTitle}</Typography>
                                  <Typography variant="caption" display="block" color="grey.300">{ev.contentId}</Typography>
                                  <Typography variant="caption" display="block">🎬 {ev.director || '—'}</Typography>
                                  <Typography variant="caption" display="block">🎥 {ev.cinematographer || '—'}</Typography>
                                  {castNames.length > 0 && (
                                    <Typography variant="caption" display="block">🎭 {castNames.join(', ')}</Typography>
                                  )}
                                  <Typography variant="caption" display="block">⏰ {ev.startTime} – {ev.endTime}</Typography>
                                  {ev.location && <Typography variant="caption" display="block">📍 {ev.location}</Typography>}
                                  {mine && <Typography variant="caption" display="block" sx={{ color: '#FFD54F', fontWeight: 700, mt: 0.5 }}>★ You are in this shoot</Typography>}
                                </Box>
                              }>
                                <Box
                                  onClick={e => { e.stopPropagation(); setPopover({ anchor: e.currentTarget, event: { ev, castNames, mine } }); }}
                                  sx={{
                                  bgcolor: col.bg, color: col.text,
                                  borderRadius: 0.5, px: 0.5, py: 0.1,
                                  fontSize: '0.6rem', fontWeight: mine ? 700 : 600,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                  outline: mine ? '2px solid #fff' : 'none',
                                  outlineOffset: '-1px'
                                }}>
                                  {mine ? '★ ' : ''}{ev.contentTitle}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail list — today+tomorrow by default, selected date when clicked */}
      <Box mt={2}>
        {loadingDetail ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
        ) : (
          detailDates.map(dateStr => {
            const evs = getDetailForDate(dateStr);
            const isToday = dateStr === todayStr;
            const isTomorrow = dateStr === tomorrowStr;
            const label = isToday ? 'Today' : isTomorrow ? 'Tomorrow'
              : new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
            return (
              <Box key={dateStr} mb={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" fontWeight={700}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                    <Chip label={`${evs.length} shoot${evs.length !== 1 ? 's' : ''}`} size="small"
                      color={evs.length ? 'primary' : 'default'} variant="outlined" />
                  </Box>
                  {selectedDate && (
                    <Chip label="Clear selection" size="small" variant="outlined"
                      onClick={() => setSelectedDate(null)} sx={{ cursor: 'pointer' }} />
                  )}
                </Box>
                {evs.length === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    No {scheduleFilter === 'mine' ? 'shoots you are part of' : 'confirmed shoots'} on {label.toLowerCase()}.
                  </Alert>
                ) : (
                  <Grid container spacing={1}>
                    {evs.map((ev, i) => {
                      const mine = isMyCrew(ev, user?.username);
                      const col  = mine ? HIGHLIGHT_COLOR : (TYPE_COLOR[ev.contentType] || DEFAULT_COLOR);
                      const castNames = Array.isArray(ev.castingList)
                        ? ev.castingList
                        : (() => { try { return JSON.parse(ev.castingList || '[]'); } catch (_) { return []; } })();
                      return (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <Card
                            variant="outlined"
                            onClick={e => setPopover({ anchor: e.currentTarget, event: { ev, castNames, mine } })}
                            sx={{ borderLeft: `4px solid ${col.bg}`, bgcolor: mine ? '#FFF3E0' : 'background.paper', cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                          >
                            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="start">
                                <Typography variant="body2" fontWeight={700} noWrap>{ev.contentTitle}</Typography>
                                {mine && <Chip label="My Shoot" size="small" sx={{ bgcolor: HIGHLIGHT_COLOR.bg, color: '#fff', fontSize: '0.55rem', height: 18, flexShrink: 0 }} />}
                              </Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                ⏰ {ev.startTime} – {ev.endTime}
                              </Typography>
                              {ev.director && <Typography variant="caption" color="text.secondary" display="block">🎬 {ev.director}</Typography>}
                              {ev.cinematographer && <Typography variant="caption" color="text.secondary" display="block">🎥 {ev.cinematographer}</Typography>}
                              {castNames.length > 0 && <Typography variant="caption" color="text.secondary" display="block" noWrap>🎭 {castNames.join(', ')}</Typography>}
                              <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                                <Chip label={ev.channel} size="small" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                                <Chip label={ev.contentType} size="small" sx={{ bgcolor: col.bg, color: 'white', fontSize: '0.6rem' }} />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            );
          })
        )}
      </Box>
      {/* Event detail popover — triggered by tap on mobile, also works on desktop */}
      <Popover
        open={Boolean(popover.anchor)}
        anchorEl={popover.anchor}
        onClose={() => setPopover({ anchor: null, event: null })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ sx: { p: 1.5, maxWidth: 260, bgcolor: '#333', color: '#fff', borderRadius: 2 } }}
      >
        {popover.event && (() => {
          const { ev, castNames, mine } = popover.event;
          return (
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>{ev.contentTitle}</Typography>
              <Typography variant="caption" display="block" sx={{ color: '#bbb' }}>{ev.contentId}</Typography>
              <Divider sx={{ my: 0.5, borderColor: '#555' }} />
              <Typography variant="caption" display="block">🎬 {ev.director || '—'}</Typography>
              <Typography variant="caption" display="block">🎥 {ev.cinematographer || '—'}</Typography>
              {castNames.length > 0 && <Typography variant="caption" display="block">🎭 {castNames.join(', ')}</Typography>}
              <Typography variant="caption" display="block">⏰ {ev.startTime} – {ev.endTime}</Typography>
              {ev.location && <Typography variant="caption" display="block">📍 {ev.location}</Typography>}
              {mine && <Typography variant="caption" display="block" sx={{ color: '#FFD54F', fontWeight: 700, mt: 0.5 }}>★ You are in this shoot</Typography>}
            </Box>
          );
        })()}
      </Popover>

      {/* Export PDF Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Shoot Plan</DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            value={exportRange} exclusive size="small" fullWidth
            onChange={(_, v) => { if (v) setExportRange(v); }}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="weekly">This Week</ToggleButton>
            <ToggleButton value="monthly">{MONTHS[month]} {year}</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {exportRange === 'weekly'
              ? 'Exports confirmed shoots for the current calendar week (Mon – Sun).'
              : `Exports all confirmed shoots for ${MONTHS[month]} ${year}.`}
          </Typography>
          <TextField
            label="Notes (optional)"
            placeholder="e.g. Please be on set by 8:00 AM. Bring costumes."
            multiline rows={3} fullWidth
            value={exportNotes}
            onChange={e => setExportNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setExportDialogOpen(false); setExportNotes(''); }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleExportPDF} disabled={exportLoading}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}
            startIcon={<PictureAsPdf />}
          >
            {exportLoading ? 'Generating…' : 'Export & Print'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default ShootCalendar;
